import { Order } from '../models/order.models';

/**
 * Identity of one tunnel tile.
 *
 * A tile's `id` is its order id, which several tiles can share: every wash on a membership
 * belongs to the same order, and a family plan's vehicles all wash on one order (CWS-1231).
 * Each tile is one LPR read, so the LPR entry id tells them apart; tiles without one (e.g.
 * rewash orders missing from the LPR list) fall back to the order id.
 */
export function tunnelRowKey(order: Pick<Order, 'id' | 'lprId'> | null | undefined): string | null {
  if (!order) {
    return null;
  }
  return order.lprId?.trim() || order.id || null;
}

/** Whether two tiles are the same tile. */
export function isSameTunnelRow(
  a: Pick<Order, 'id' | 'lprId'> | null | undefined,
  b: Pick<Order, 'id' | 'lprId'> | null | undefined
): boolean {
  const keyA = tunnelRowKey(a);
  return keyA !== null && keyA === tunnelRowKey(b);
}

/**
 * The newest tile for an order (tiles are listed oldest first), so a new wash on an order
 * that already has tiles highlights the wash that just happened.
 */
export function findLatestTunnelRowForOrder<T extends Pick<Order, 'id' | 'orderId'>>(
  rows: T[],
  orderId: string | null | undefined
): T | undefined {
  if (!orderId) {
    return undefined;
  }
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (rows[i].id === orderId || rows[i].orderId === orderId) {
      return rows[i];
    }
  }
  return undefined;
}
